/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { authInfoMapper } from 'src/core/handlers/auth/authInfoMappers';
import { loadTenants } from 'src/core/handlers/azure/azureHandlerGroup';
import { tokenSwaggerToTokenString } from 'src/core/handlers/azure/azureMappers';
import { updateCsAccountStoreInSession } from 'src/core/handlers/tokensHandlerGroup';
import { getAuthService } from 'src/core/services/authService';
import { getTokensService } from 'src/core/services/tokensService';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { userMigrationInfoSwaggerToUserMigrationInfoStores } from 'src/verticals/azureMigrationModal/handlers/azureMigrationMappers';

export type AuthHandlerGroup = ReturnType<typeof createAuthHandlerGroup>;

const authService = getAuthService();
const tokensService = getTokensService();
const sessionStorage = getStorage('sessionStorage');

/**
 * Represents a handler group for authentication operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createAuthHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    /**
     * Loads Azure and user related data that enable the application
     * to startup correctly.
     *
     * @see authFlow.md for more details.
     * @returns A boolean value to indicate whether to shortcut
     * auth store updates or not. Check `authFlow.md` for more details.
     */
    const onLogin = async (): Promise<boolean> => {
        let neutralArmToken: string = '';
        const { loadingState, session } = rootStore.azureStore;

        try {
            let { tenantId } = session;

            loadingState.setState('running');
            neutralArmToken = tokenSwaggerToTokenString(await tokensService.getNeutralArmToken());
            session.setNeutralToken(neutralArmToken);

            const tenants = await loadTenants(neutralArmToken, rootStore);
            const query = authService.getURLSearchParamsCaseInsensitive(window.location.search);
            const tenantIdFromSearch = query.get('tenantid') || undefined;
            const isCachedTenantIdInvalid =
                !tenantId || tenants?.every(tenant => tenant.id !== tenantId) || (tenantIdFromSearch && tenantIdFromSearch !== tenantId);
            const { defaultTenantId } = rootStore.userStore;
            tenantId = isCachedTenantIdInvalid
                ? tenants?.find(t => t.id === tenantIdFromSearch)
                    ? tenantIdFromSearch
                    : tenants?.find(t => t.id === defaultTenantId)
                    ? defaultTenantId
                    : tenants?.[0].id
                : tenantId;

            if (sessionStorage.hasData(LuisConstants.StorageKeys.TenantSignInStorageKey)) {
                sessionStorage.removeData(LuisConstants.StorageKeys.TenantSignInStorageKey);
                authService.login({ tenantId });

                return false;
            }
            const [armToken, storageToken] = await Promise.all([
                tokensService.getTenantArmToken({ tenantId }).then(tokenSwaggerToTokenString),
                tokensService.getAzureStorageAccountToken({ tenantId }).then(tokenSwaggerToTokenString)
            ]);

            session.setTenantId(tenantId);
            session.setUserTokens({ armToken, storageToken });
            session.setServiceKey({ keyType: 'none', value: null, userRole: 'none', displayedUserRole: 'none' });

            try {
                await updateCsAccountStoreInSession(rootStore);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`Failed to update CS account with error: ${(<Error>e).message}`);
            }

            loadingState.setState('completed');
        } catch (error) {
            loadingState.setState('failed', <Error>error);
            // eslint-disable-next-line no-console
            console.error(`Failed to sign in with error:  ${(<Error>error).message}`);

            throw error;
        }

        return true;
    };

    /**
     * Logs in to LUIS.
     */
    const login = async (params?: HandlerParams<{ selectAccount?: boolean }>) => {
        const { selectAccount } = params ?? { selectAccount: false };

        sessionStorage.setData(LuisConstants.StorageKeys.TenantSignInStorageKey, 'true');
        authService.login({ selectAccount });
    };

    /**
     * Logs out from LUIS. Clears the Azure session
     * information from the local storage.
     */
    const logout = async () => {
        getStorage('localStorage').removeData(LuisConstants.StorageKeys.AzureSessionStorageKey);
        authService.logout();
    };

    /**
     * Checks whether the user is currently logged
     * in or not.
     */
    const checkLoginStatus = async (_?: HandlerParams) => {
        try {
            rootStore.authStore.setIsLoading(true);
            rootStore.setUserStore(authInfoMapper(await authService.checkLoginStatus()));

            const shouldSkip = await onLogin();

            if (!shouldSkip) {
                return;
            }

            rootStore.authStore.setIsSignedIn(true);
            rootStore.authStore.setIsLoading(false);
        } catch (error) {
            rootStore.authStore.setIsLoading(false);

            if (rootStore.authStore.isSignedIn) {
                throw error;
            }
        }
    };

    const getUserMigrationInfo = async () => {
        const { tracker } = rootStore.userMigrationInfoStoreHouse;

        if (tracker.isRunning || tracker.isCompleted) {
            return;
        }

        tracker.setState('running');

        try {
            const swaggers = await authService.getUserMigrationInfo();
            const userMigrationInfo = swaggers.map(userMigrationInfoSwaggerToUserMigrationInfoStores);

            rootStore.userMigrationInfoStoreHouse.setItems(userMigrationInfo);
            tracker.setState('completed');
        } catch {
            tracker.setState('failed');
        }
    };

    return { login, logout, checkLoginStatus, getUserMigrationInfo };
};
